import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

export default class extends Controller {
  static targets = ["locationInput", "spaceInput", "goToLocation", "goToSpace", "tip"]
  static values = {
    searchLocationsUrl: String,
    loadSpacesUrl: String,
    locationUrl: String
  }

  connect() {
    this.selectedLocationId = null
    this.initLocationSelect()
    this.initSpaceSelect([])
    this.disableSpaceSelect()
  }

  disconnect() {
    this.locationSelect?.destroy()
    this.spaceSelect?.destroy()
  }

  initLocationSelect() {
    this.locationSelect = new window.TomSelect(this.locationInputTarget, {
      valueField: 'value',
      labelField: 'text',
      searchField: [],
      maxItems: 1,
      highlight: true,
      load: (query, callback) => {
        this.locationSelect.clearOptions()
        this.fetchLocations(query, callback)
      },
      render: {
        option: (item, escape) => this.renderLocationOption(item, escape),
        no_results: () => `<div class="no-results">${window.I18n.t('views.admin_home.search.no_results')}</div>`
      },
      onChange: (value) => this.onLocationChange(value)
    })

    const labelledBy = this.locationInputTarget.getAttribute('aria-labelledby')
    if (labelledBy) {
      this.locationSelect.control_input.setAttribute('aria-labelledby', labelledBy)
    }
  }

  initSpaceSelect(spaces) {
    if (this.spaceSelect) {
      this.spaceSelect.destroy()
      this.spaceSelect = null
    }

    this.spaceInputTarget.disabled = false

    const placeholder = spaces.length > 0
      ? window.I18n.t('views.admin_home.search.space_active_placeholder')
      : window.I18n.t('views.admin_home.search.space_placeholder')

    this.spaceSelect = new window.TomSelect(this.spaceInputTarget, {
      valueField: 'value',
      labelField: 'text',
      searchField: ['text'],
      maxItems: 1,
      options: spaces,
      optgroupField: 'optgroup',
      optgroups: [
        { value: 'g5', label: 'G5' },
        { value: 'g7', label: 'G7' }
      ],
      placeholder: placeholder,
      render: {
        option: (item, escape) => this.renderSpaceOption(item, escape),
        optgroup_header: (data, escape) => `<div class="optgroup-header">${escape(data.label)}</div>`,
        no_results: () => `<div class="no-results">${window.I18n.t('views.admin_home.search.no_results')}</div>`
      },
      onChange: (value) => this.onSpaceChange(value)
    })
  }

  disableSpaceSelect() {
    if (this.spaceSelect) {
      this.spaceSelect.disable()
    }
    this.updateGoToSpaceState()
  }

  enableSpaceSelect() {
    if (this.spaceSelect) {
      this.spaceSelect.enable()
    }
  }

  async fetchLocations(query, callback) {
    try {
      const response = await get(this.searchLocationsUrlValue, { query: { q: query }, responseKind: 'json' })
      if (response.ok) {
        callback(await response.json)
      } else {
        callback()
      }
    } catch (_error) {
      callback()
    }
  }

  async loadSpaces() {
    try {
      const response = await get(this.loadSpacesUrlValue, {
        query: { location_id: this.selectedLocationId },
        responseKind: 'json'
      })
      if (response.ok) {
        const spaces = await response.json
        this.initSpaceSelect(spaces)
        this.enableSpaceSelect()
      }
    } catch (_error) {
      // silently fail
    }
  }

  renderLocationOption(item, escape) {
    const owner = item.owner ? `<span class="location-search__result-owner">${escape(item.owner)}</span>` : ''
    return `<div class="location-search__result">
      <span class="location-search__result-name">${escape(item.text)}</span>
      ${owner}
    </div>`
  }

  renderSpaceOption(item, escape) {
    const typeLabel = item.type === 'g5' ? 'G5' : 'G7'
    const modelLabel = item.model ? ` ${escape(item.model)}` : ''
    return `<div class="space-search__result">
      <span class="space-search__result-name">${escape(item.text)}</span>
      <span class="space-search__result-type">${typeLabel}${modelLabel}</span>
    </div>`
  }

  onLocationChange(value) {
    this.selectedLocationId = value || null
    this.selectedSpaceUrl = null

    if (value) {
      this.goToLocationTarget.classList.remove('admin-home__search-btn--disabled')
      this.loadSpaces()
      // Focus the "Go to location" button so the user can hit Enter to navigate
      setTimeout(() => this.goToLocationTarget.focus(), 50)
    } else {
      this.goToLocationTarget.classList.add('admin-home__search-btn--disabled')
      this.initSpaceSelect([])
      this.disableSpaceSelect()
    }

    if (this.hasTipTarget) {
      this.tipTarget.style.display = value ? 'none' : ''
    }

    this.updateGoToSpaceState()
  }

  onSpaceChange(value) {
    if (value && this.spaceSelect) {
      const option = this.spaceSelect.options[value]
      this.selectedSpaceUrl = option?.url || null
    } else {
      this.selectedSpaceUrl = null
    }
    this.updateGoToSpaceState()
  }

  updateGoToSpaceState() {
    if (this.hasGoToSpaceTarget) {
      if (this.selectedSpaceUrl) {
        this.goToSpaceTarget.classList.remove('admin-home__search-btn--disabled')
      } else {
        this.goToSpaceTarget.classList.add('admin-home__search-btn--disabled')
      }
    }
  }

  goToLocation() {
    if (this.selectedLocationId) {
      window.location.href = `${this.locationUrlValue}/${this.selectedLocationId}`
    }
  }

  goToSpace() {
    if (this.selectedSpaceUrl) {
      window.location.href = this.selectedSpaceUrl
    }
  }
}
